import React from 'react';

const ContactModal = React.lazy(() => import('./ContactModal').then((module) => ({ default: module.ContactModal })));

type DeferredContactModalProps = {
  children?: React.ReactElement<{ onClick?: React.MouseEventHandler }>;
  defaultMessage?: string;
  defaultPack?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * The contact form includes focus management and scroll locking. Those are only
 * useful after a visitor asks to contact us, so keep them out of the first paint.
 */
export function DeferredContactModal({ children, open, onOpenChange, ...props }: DeferredContactModalProps) {
  const [requested, setRequested] = React.useState(open === true);
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const controlled = open !== undefined;
  const visible = controlled ? open : uncontrolledOpen;

  React.useEffect(() => {
    if (open) setRequested(true);
  }, [open]);

  const changeOpen = (next: boolean) => {
    if (!controlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  if (!requested) {
    if (!children) return null;
    return React.cloneElement(children, {
      onClick: (event: React.MouseEvent) => {
        children.props.onClick?.(event);
        if (event.defaultPrevented) return;
        setRequested(true);
        changeOpen(true);
      },
    });
  }

  return (
    <React.Suspense fallback={children ?? null}>
      <ContactModal {...props} open={visible} onOpenChange={changeOpen}>
        {children}
      </ContactModal>
    </React.Suspense>
  );
}
